import exterior from "../images/example-images/extPanel.jpg";
import wheels from "../images/example-images/wheel.jpg";
import interior from "../images/example-images/glass.jpg";
import mechanical from "../images/example-images/mechElectrical.jpg";
import tyres from "../images/example-images/tyre.jpg";
import { assumptionType, validationMessages } from "../data/filterTypes";
import dayjs from "dayjs";
import {
  QuestionIds,
  questionPresentationContent
} from "../data/QuestionPresentation";

// Ensure only first letter is capitalised 
export const formatLabel = (label) => {
  if (!label) return "";
  const trimmed = label.trim();
  return trimmed.charAt(0).toUpperCase() + trimmed.slice(1).toLowerCase();
};

export const formatAnswers = (answers) =>
  answers.map((answer) => ({
    id: answer.id,
    label: formatLabel(answer.content),
    tooltip: answer.tooltip,
    imageQuantityRequired: answer.imageQuantityRequired,
    answerOrder: answer.answerOrder
  }));
export const formatQuestions = (questions, quickValuation, isCnp = false) =>
  questions?.map(({ category, questions }) => ({
    category,
    questions: questions.map(({ id, answers, metadata, ...question }) => {
      if (id === QuestionIds.INTERIOR) {
        return {
          id,
          stepNo: quickValuation ? 3 : 9,
          footerLabel:
            "And finally, give us some info about the mechanics and electrics.",
          answers: [...formatAnswers(answers)],
          metadata: metadata ? JSON.parse(metadata) : null,
          ...question
        };
      }
      if (id === QuestionIds.SERVICEHISTORY) {
        return {
          id,
          footerLabel: "Great - Just a few more questions.",
          stepNo: quickValuation ? 3 : 4,
          answers: [...formatAnswers(answers)],
          metadata: metadata ? JSON.parse(metadata) : null,
          ...question
        };
      }
      if (id === QuestionIds.TAXI) {
        return {
          id,
          footerLabel: "Now let's move on to the car's condition.",
          stepNo: quickValuation ? 3 : 5,
          answers: [...formatAnswers(answers)],
          metadata: metadata ? JSON.parse(metadata) : null,
          ...question
        };
      }
      if (id === QuestionIds.EXTERIORBODYWORK) {
        return {
          id,
          footerLabel: "Thanks! Now let's have a look at the tyres.",
          stepNo: quickValuation ? 3 : 6,
          answers: [...formatAnswers(answers)],
          metadata: metadata ? JSON.parse(metadata) : null,
          ...question
        };
      }
      if (id === QuestionIds.TYRES) {
        return {
          id,
          stepNo: quickValuation ? 3 : 7,
          answers: [...formatAnswers(answers)],
          metadata: metadata ? JSON.parse(metadata) : null,
          ...question
        };
      }
      if (id === QuestionIds.MECHANICSELECTRICS) {
        return {
          id,
          stepNo: quickValuation ? 3 : 10,
          footerLabel:
            quickValuation || isCnp
              ? "Great, let’s get your valuation!"
              : "Great, let's get some pictures!",
          answers: [...formatAnswers(answers)],
          metadata: metadata ? JSON.parse(metadata) : null,
          ...question
        };
      }
      if (id === QuestionIds.WHEELS) {
        return {
          id,
          stepNo: quickValuation ? 3 : 8,
          answers: [...formatAnswers(answers)],
          metadata: metadata ? JSON.parse(metadata) : null,
          ...question
        };
      }
      if (id === QuestionIds.KEYS) {
        return {
          id,
          stepNo: quickValuation ? 3 : 3,
          answers: [...formatAnswers(answers)],
          metadata: metadata ? JSON.parse(metadata) : null,
          ...question
        };
      }
      return {
        id,
        answers: [...formatAnswers(answers)],
        metadata: metadata ? JSON.parse(metadata) : null,
        ...question
      };
    })
  }));

//   keep until we see shape of real data
export const handleNotSureId = (id, selectedAnswers) => {
  if (id === -1) {
    return selectedAnswers;
  } else {
    return id;
  }
};

export const getExteriorLabel = (answerId, requiredPhotos, pluralise) => {
  const pluraliseText = requiredPhotos > 1 ? "have" : "has";
  if (answerId === 57) {
    return "there is a poor paint job or wrap.";
  } else if (answerId === 38) {
    return `${requiredPhotos} or more panels have imperfections.`;
  } else {
    return `${requiredPhotos} panel${pluralise} ${pluraliseText} imperfections.`;
  }
};

export const getExteriorboldHeaderTextText = (
  answerId,
  requiredPhotos,
  pluralise
) => {
  if (answerId !== 57) {
    return `${requiredPhotos} panel${pluralise}`;
  } else {
    return "poor paint job";
  }
};

export const getMechanicalboldHeaderText = (answerId) => {
  if (answerId === 66) {
    return "non runner";
  }
  if (answerId === 83) {
    return "warning lights";
  }
  if (answerId === 84) {
    return "electrical fault";
  }
  if (answerId === 85) {
    return "unusual noise";
  }
  if (answerId === 65) {
    return "modified";
  }
  if (answerId === 49) {
    return "warning lights";
  }
  if (answerId === 48) {
    return "minor";
  }
};
export const getMechanicalLabel = (answerId) => {
  if (answerId === 66) {
    return "your car is a non runner";
  }
  if (answerId === 83) {
    return "there are warning lights on the dashboard";
  }
  if (answerId === 84) {
    return "there is an electrical fault e.g sat nav, faulty ignition switch or something else";
  }
  if (answerId === 85) {
    return "your car makes an unusual noise e.g hissing, rattling, ticking or something else";
  }
  if (answerId === 65) {
    return "your car has been modified";
  }
  if (answerId === 49) {
    return "there are warning lights on the dashboard";
  }
  if (answerId === 48) {
    return "there are minor problems with the car's mechanical and electrical parts";
  }
};

export const getInteriorLabel = (answerId) => {
  if (answerId === 64) {
    return "more than one large imperfection";
  }
  if (answerId === 46) {
    return "poor";
  }
  if (answerId === 45) {
    return "average";
  }
  if (answerId === 44) {
    return "good";
  }
  if (answerId === 43) {
    return "excellent";
  }
};

// Should take in answerId and the label should be photo copy from google sheet
export const getPhotoData = (questionID, requiredPhotos, answerId) => {
  const pluralise = requiredPhotos > 1 ? "s" : "";
  const boldLabelText = `${requiredPhotos} photo${pluralise}`;

  const pluralHelperTextWord = requiredPhotos > 1 ? "the" : "that";

  const exteriorData = {
    label: `You've told us ${getExteriorLabel(
      answerId,
      requiredPhotos,
      pluralise
    )}`,
    helperText: `at least ${boldLabelText} detailing this.`,
    galleryHelperText: `an image of the panel`,
    image: exterior,
    showPreviewImage: false,
    singlePhotoUploadMore: true,
    tag: "Exterior",
    id: "Exterior",
    imageQuantityRequired: requiredPhotos,
    photoIntroText: `${requiredPhotos} photo${pluralise} of the panel${pluralise}`,
    boldLabelText,
    boldHeaderText: `${getExteriorboldHeaderTextText(
      answerId,
      requiredPhotos,
      pluralise
    )}`,
    boldHeaderTextAdditional: "wrap",
    order: 1
  };

  const wheelsAndTyresAmountText = requiredPhotos > 3 ? "all" : requiredPhotos;
  const pluralWheelsAndTyres = requiredPhotos > 1 ? "" : "s";
  const pluralHelperText = requiredPhotos > 1 ? "at least " : "";

  const tyresData = {
    label: `You've told us ${wheelsAndTyresAmountText} tyre${pluralise} need${pluralWheelsAndTyres} to be replaced.`,
    helperText: `at least ${pluralHelperText}${boldLabelText} of ${pluralHelperTextWord} tyre${pluralise}.`,
    image: tyres,
    showPreviewImage: false,
    singlePhotoUploadMore: true,
    tag: "Tyres",
    id: "Tyres",
    imageQuantityRequired: requiredPhotos,
    photoIntroText: `${requiredPhotos} photo${pluralise} of the tyre${pluralise}`,
    boldLabelText,
    boldHeaderText: `${wheelsAndTyresAmountText} tyre${pluralise}`,
    order: 2
  };

  const wheelsData = {
    label: `You've told us ${wheelsAndTyresAmountText} wheel${pluralise} need${pluralWheelsAndTyres} to be repaired.`,
    helperText: `at least ${pluralHelperText}${boldLabelText} of ${pluralHelperTextWord} wheel${pluralise}.`,
    image: wheels,
    showPreviewImage: false,
    singlePhotoUploadMore: true,
    tag: "Wheels",
    id: "Wheels",
    imageQuantityRequired: requiredPhotos,
    photoIntroText: `${requiredPhotos} photo${pluralise} of ${pluralHelperTextWord} wheel${pluralise}`,
    boldLabelText,
    boldHeaderText: `${wheelsAndTyresAmountText} wheel${pluralise}`,
    order: 3
  };

  const ofTheDasboard =
    parseInt(answerId) === 83 ? "of the dashboard" : "detailing this";

  const mechanicalData = {
    label: `You've told us ${getMechanicalLabel(parseInt(answerId))}.`,
    helperText: `at least ${requiredPhotos} photo${pluralise} ${ofTheDasboard}.`,
    galleryHelperText: `an image of the mechanical or electrical parts`,
    image: mechanical,
    showPreviewImage: false,
    singlePhotoUploadMore: true,
    tag: "Mechanical & Electrical",
    id: "Mechanical & Electrical",
    imageQuantityRequired: requiredPhotos,
    photoIntroText: `${requiredPhotos} photo${pluralise} showing warning lights`,
    boldLabelText,
    boldHeaderText: getMechanicalboldHeaderText(parseInt(answerId)),
    order: 5
  };

  const isThis = requiredPhotos === 1 ? "this" : "them";

  const interiorData = {
    label: `You've told us the condition of your car's interior is ${getInteriorLabel(
      answerId
    )}.`,
    helperText: `at least ${requiredPhotos} photo${pluralise} of the interior.`,
    galleryHelperText: `an image of the interior`,
    image: interior,
    showPreviewImage: false,
    singlePhotoUploadMore: true,
    tag: "Interior",
    id: "Interior",
    imageQuantityRequired: requiredPhotos,
    photoIntroText: `${requiredPhotos} photo${pluralise} of the interior`,
    boldLabelText,
    boldHeaderText: getInteriorLabel(answerId),
    order: 4
  };

  const glassData = {
    label: "You've told us your cars glass is damaged.",
    helperText: `at least ${requiredPhotos} photo${pluralise} detailing this.`,
    galleryHelperText: `an image of the glass`,
    image: interior,
    showPreviewImage: false,
    singlePhotoUploadMore: true,
    tag: "Glass",
    id: "Glass",
    imageQuantityRequired: requiredPhotos,
    photoIntroText: `${requiredPhotos} photo${pluralise} of the glass`,
    boldLabelText,
    boldHeaderText: "glass",
    order: 6
  };
  switch (parseInt(questionID)) {
    case 14:
      return exteriorData;
    case 15:
      return tyresData;
    case 18:
      return wheelsData;
    case 16:
      return interiorData;
    case 17:
      return mechanicalData;
    case 23:
      return glassData;
    default:
      return "";
  }
};

export const getDefaultAnswers = (questions) =>
  questions
    ? questions.reduce(
        (prev, { id, selectedAnswers }) => ({
          ...prev,
          [id]: selectedAnswers
        }),
        {}
      )
    : {};

export const getCurrentAnswerOrDefault = (
  questions,
  currentQuestionsAndAnswers
) =>
  questions
    ? questions.reduce(
        (prev, { id, selectedAnswers }) => ({
          ...prev,
          [id]: currentQuestionsAndAnswers[id]
            ? currentQuestionsAndAnswers[id]
            : selectedAnswers
        }),
        {}
      )
    : {};

export const removeDefaultAnswers = (
  currentQuestionsAndAnswersGroup,
  questionCategory
) => {
  return Object.fromEntries(
    Object.entries(currentQuestionsAndAnswersGroup).filter(
      ([key]) =>
        currentQuestionsAndAnswersGroup[key].toString() !==
        questionCategory
          .find((question) => parseInt(key) === question.id)
          ?.selectedAnswers.toString()
    )
  );
};

export const getCurrentAnswersByIds = (
  currentQuestionsAndAnswers,
  idsToFilter
) => {
  return Object.keys(currentQuestionsAndAnswers)
    .filter((id) => idsToFilter.includes(parseInt(id)))
    .reduce((obj, key) => {
      return {
        ...obj,
        [key]: currentQuestionsAndAnswers[key]
      };
    }, {});
};

export const getPrePopulatedAnswers = (groups) =>
  groups
    .map(({ questions }) => getDefaultAnswers(questions))
    .reduce((prev, curr) => ({ ...prev, ...curr }), {});

export const getDefaultQuestionAndAnswerSetIDs = (groups) =>
  groups
    .map(({ questions }) => getCurrentAnswersIDs(questions))
    .reduce((prev, curr) => ({ ...prev, ...curr }), {});

export const getCurrentAnswersIDs = (questions) =>
  questions
    ? questions.reduce(
        (prev, { id, answers }) => ({
          ...prev,
          [id]: answers.map((answer) => answer.id)
        }),
        {}
      )
    : {};

export const getPrePopulatedOrUpdatedAnswers = (
  groups,
  currentQuestionsAndAnswers
) =>
  groups
    .map(({ questions }) =>
      getCurrentAnswerOrDefault(questions, currentQuestionsAndAnswers)
    )
    .reduce((prev, curr) => ({ ...prev, ...curr }), {});

export const getChosenAnswer = (question, questionsAndAnswers) => {
  const chosenAnswerId = questionsAndAnswers[question.id][0];
  return chosenAnswerId;
};

export const getConditionalAnswerCategory = (id) => {
  const conditionalAnswerCategories = {
    14: "Exterior bodywork",
    15: "Tyres",
    16: "Interior",
    17: "Mechanical & electrical",
    18: "Wheels",
    22: "Missing items",
    23: "Glass",
    24: "Modifications"
  };
  return conditionalAnswerCategories[id];
};

export const getAnswersReviewAction = (id) => {
  const answersReviewCategories = {
    6: "no of keys",
    2: "service history",
    11: "taxi question",
    14: "exterior bodywork",
    15: "tyres",
    18: "wheels",
    16: "interior",
    17: "mechanical & electrical"
  };
  return answersReviewCategories[id];
};

export const getLowestAmount = (array, attribute) => {
  const lowest = array
    .filter((element) => element[attribute])
    .sort((a, b) => a - b)[0];
  if (lowest !== undefined) {
    return lowest[attribute];
  }
};

export const hasProperty = (array, property) =>
  array.some((element) => element[property]);

export const getUsersAnswers = (categories) =>
  categories
    .map((category) =>
      category.questions.reduce(
        (prev, { id, selectedAnswers }) => ({
          ...prev,
          [id]: selectedAnswers
        }),
        {}
      )
    )
    .reduce((prev, current) => ({ ...prev, ...current }), {});

export const hasAnswersChanged = (prevAnswers, newAnswers) =>
  !Object.keys(prevAnswers).every(
    (key) => prevAnswers[key].toString() === newAnswers[key].toString()
  );

export const isArray = (value) => {
  return Array.isArray(value);
};

export const paramBuilder = (registration, mileage, appraisalSessionId) => {
  return `?registration=${registration}&mileage=${mileage}&appraisalSessionId=${appraisalSessionId}`;
};

export const isAssumptionDisabled = (content) => {
  const motOrPrevOwnersKeyWords = [
    assumptionType.MOT,
    assumptionType.PREVIOUSOWNERS
  ];
  return motOrPrevOwnersKeyWords.some((keyWord) =>
    content.toLowerCase().includes(keyWord)
  );
};

export const isVehicleCommercialVehicle = (vehicle) =>
  vehicle?.isCommercialVehicle || vehicle?.capCode?.slice(-1) === "L";

export const showCardRef = (cardRef) =>
  (cardRef.current.style.display = "block");

export const showCardRefFlex = (cardRef) =>
  (cardRef.current.style.display = "flex");

export const hideCardRef = (cardRef) =>
  (cardRef.current.style.display = "none");

export const convertToBool = (str) => str === "true";

export const clearState = (state) => state(null);

export const HPIRegCheck = new RegExp(
  "^([0-9]{3,5}|[0-9]{1,4}[A-Z]{1,3}|[A-Z]{1,3}[0-9]{1,4}|[A-Z]{2}[0-9]{6}|[A-Z][0-9]{5,6}|[A-Z]{3}[0-9]{1,3}[A-Z]|[A-Z][0-9]{1,3}[A-Z]{3}|[0-9]{3}[A-Z][0-9]{3}|[A-Z]{2}[0-9]{2}[A-Z]{3})$",
  "i"
);

export const helperTextObject = {
  front: "an angled view from the front.",
  right: "a view from the right side of the vehicle.",
  left: "a view from the left side of the vehicle.",
  back: "a view from the back of the vehicle.",
  mileage: "a photo of the vehicle's mileage.",
  Exterior: "1 photo detailing this.",
  Tyres: "1 photo of that tyre.",
  Wheels: "1 photo of that wheel.",
  "Interior & Glass": "at least 1 photo of this.",
  "Mechanical & Electrical": "at least 1 photo of the interior."
};

export const nodeContainsText = (content, node) => {
  const hasText = (node) => node.textContent === content;
  const nodeHasText = hasText(node);
  const childrenDontHaveText = Array.from(node.children).every(
    (child) => !hasText(child)
  );
  return nodeHasText && childrenDontHaveText;
};

export const howManyDaysFromNow = (now, date) => {
  const dateAtEndOfDay = dayjs(date).hour(23);
  const nowAtStartOfDay = dayjs(now).hour(0);
  return Math.abs(nowAtStartOfDay.diff(dateAtEndOfDay, "day"));
};

export const handleEnterCode = (promoCardRef) => {
  showCardRef(promoCardRef);
  const addPromotionalBonusInput =
    promoCardRef.current.getElementsByTagName("input")[0];
  addPromotionalBonusInput.focus();
};

export const openWhatHappensNextModal = (whatHappensNextCardRef) => {
  showCardRef(whatHappensNextCardRef);
  const showWhatHappensNextModal =
    whatHappensNextCardRef.current.getElementsByTagName("div")[0];
  showWhatHappensNextModal.focus();
};

export const openTellUsMoreModal = (tellUsMoreCardRef) => {
  showCardRefFlex(tellUsMoreCardRef);
  const tellUsMoreModal =
    tellUsMoreCardRef.current.getElementsByTagName("div")[0];
  tellUsMoreModal.focus();
};

export const getValidationMessage = (answers) => {
  const validationMessage =
    answers.length === 5
      ? validationMessages.SCALE
      : validationMessages.STANDARD;
  return validationMessage;
};

export const isQuestionAnswered = (question, storedQuestionsAndAnswers) => {
  return (
    storedQuestionsAndAnswers[question.id]?.length > 0 &&
    storedQuestionsAndAnswers[question.id][0] !==
      questionPresentationContent[question.id]?.component?.content
        ?.showSelectValue &&
    storedQuestionsAndAnswers[question.id][0] !==
      questionPresentationContent[question.id]?.component?.content
        ?.showCheckboxValue
  );
};

export const isQuestionsAnswered = (questions, questionsAndAnswers) => {
  return questions?.every((question) =>
    isQuestionAnswered(question, questionsAndAnswers)
  );
};

export const getPhotoQuantityMultipleChoice = (answers, userAnswers) => {
  let total = 0;
  answers.forEach((answer) => {
    if (
      userAnswers.includes(answer.id.toString()) ||
      userAnswers.includes(answer.id)
    ) {
      total += answer?.imageQuantityRequired;
    }
  });
  return total;
};

export const getPhotoQuantity = (answers, userAnswer) => {
  return answers.find(
    (answer) => answer.id.toString() === userAnswer.toString()
  )?.imageQuantityRequired;
};

export const getSelectedAnswers = (questions, selectedQuestionId) => {
  return questions.find((q) => q.id === selectedQuestionId).selectedAnswers;
};

export const getQuestionsByCategory = (questions, category) => {
  return questions.find((q) => q.category === category).questions;
};

export const hasAnalyticSearchParamInURL = (
  location,
  analyticsSearchParamsType,
  analyticSearchParam
) =>
  location.href.includes(`${analyticsSearchParamsType}=${analyticSearchParam}`);

export const capitaliseFirstLetters = (string) => {
  const splitString = string.split(" ");
  return splitString
    .map((word) => {
      return word[0].toUpperCase() + word.substring(1);
    })
    .join(" ");
};

export const sortAnswers = (answers) => {
  const noOrderSet = answers.every((answer) => answer.answerOrder === 0);
  if (noOrderSet) {
    return answers;
  } else {
    return [...answers].sort((a, b) => a.answerOrder - b.answerOrder);
  }
};
